import DashboardBoxTitle from "components/atoms/title/DashboardBoxTitle";
import DashboardBoxLayout from "components/templates/dashboard-box-layout/DashboardBoxLayout";
import { customStylesLightBorder } from "lib/helpers/configs/customStyles";
import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import Creatable from "react-select/creatable";
import { useLeadFeedbacks } from "lib/hooks/queries/Statistics/LeadFeedbacks/useLeadFeedbacks";
import ConditionalRender from "components/atoms/conditional-render/ConditionalRender";
import LoadingScreen from "components/atoms/loading-screen/LoadingScreen";
import PendingsChart from "../AllCharts/PendingsChart";
import DynamicDropdown from "components/molecules/dynamic-dropdown/DynamicDropdown";
import Filter from "../Dropdowns/Filter";
import FilterBorderedWrapper from "components/atoms/filter-bordered-wrapper/FilterBorderedWrapper";
import Icon from "components/atoms/icon/Icon";
import { IconType } from "lib/helpers/constants/iconTypes";
import { usePermissions } from "lib/hooks/shared/usePermissions";

export interface IStatisticsProps {
    fetchedUsers: string[]
}

const LeadStatistics: FC<IStatisticsProps> = ({ fetchedUsers }) => {

    const [Filters, setFilters] = useState({
        worker: 'All agents',
        filter_on: 'Today',
        from_date_of_creation: undefined,
        to_date_of_creation: undefined,
    })

    const hasPermissionToViewLeadsFeedback = usePermissions(['statistics_leads_feedback_view']);

    const { t } = useTranslation();

    const { data: leadData, isLoading } = useLeadFeedbacks(Filters);

    const [hoveredSlice, setHoveredSlice] = useState<string | null>(null);

    const [filterTableOpen, setFilterTableOpen] = useState<boolean>(false);


    return (
        <DashboardBoxLayout>
            <div className="flex flex-row gap-3 items-center justify-between mb-5">
                <div className="flex-1">
                    <DashboardBoxTitle
                        title={`Leads`}
                    />
                </div>
                <div className="flex-none">
                    <div className={`${filterTableOpen && "relative"}`}>
                        <FilterBorderedWrapper
                            className="h-[48px] w-[48px] cursor-pointer"
                            onClick={() => {
                                setFilterTableOpen(!filterTableOpen);
                            }}
                        >
                            <Icon iconType={IconType.FILTER_ICON} />
                        </FilterBorderedWrapper>
                        <ConditionalRender condition={filterTableOpen}>
                            <DynamicDropdown
                                setIsOpen={setFilterTableOpen}
                                showCloseButton={true}
                                title={t('Filter')}
                                className="sm:!w-[350px]"
                            >
                                <Filter
                                    setIsOpen={setFilterTableOpen}
                                    setFilterData={setFilters}
                                    FilterData={Filters}
                                />
                            </DynamicDropdown>
                        </ConditionalRender>
                    </div>
                </div>
            </div>
            <ConditionalRender condition={hasPermissionToViewLeadsFeedback}>
                <div className="flex flex-col gap-3 items-center mb-5 md:flex-row">
                        <Creatable<any, false>
                            placeholder={t('Choose Salesman')}
                            options={[
                                ...[
                                    { name: t('All agents'), id: 'All agents' },
                                    ...fetchedUsers,
                                ].map((user: any) => ({
                                    label: user?.name,
                                    value: user?.id,
                                })),
                            ]}
                            value={{
                                value: Filters.worker,
                                label: (fetchedUsers as any).find((user: any) => user.id === Filters.worker)?.name || t('All agents'),
                            }}
                            className="cursor-pointer font-inter-regular react-select-container min-w-[300px]"
                            classNamePrefix="react-select"
                            onChange={(newValue: any) => {
                                setFilters((prevFilters) => ({
                                    ...prevFilters,
                                    worker: newValue.value,
                                }));
                            }}
                            styles={customStylesLightBorder}
                            isValidNewOption={() => false}
                            menuPosition={"fixed"}
                            menuPortalTarget={document.body}
                        />
                </div>
            </ConditionalRender>

            <div className="flex flex-col gap-20 lg:items-center mb-5 lg:flex-row relative">

                {isLoading ? <div className="py-10"> <LoadingScreen /></div> : (

                    <ConditionalRender condition={leadData !== undefined || leadData !== null}>
                        <div className="basis-1/3 overflow-hidden">
                            <PendingsChart
                                setHoveredSlice={setHoveredSlice}
                                data={[
                                    { name: 'Unprocessed', y: ((leadData as any).data?.unprocessed / (leadData as any).data?.total) * 100, z: 100 },
                                    { name: 'Not Reached', y: ((leadData as any).data?.not_reached / (leadData as any).data?.total) * 100, z: 100 },
                                    { name: 'In Contact', y: ((leadData as any).data?.in_contact / (leadData as any).data?.total) * 100, z: 100 },
                                    { name: 'Appointment', y: ((leadData as any).data?.appointment / (leadData as any).data?.total) * 100, z: 100 },
                                    { name: 'Lost', y: ((leadData as any).data?.lost / (leadData as any).data?.total) * 100, z: 100 },
                                    { name: 'New Customer', y: ((leadData as any).data?.new_customer / (leadData as any).data?.total) * 100, z: 100 }
                                ]} />
                        </div>

                        <div className="basis-2/3">
                            <div className="grid-cols-2 grid gap-3">
                                {[
                                    { status: 'Unprocessed', count: (leadData as any).data?.unprocessed },
                                    { status: 'Not Reached', count: (leadData as any).data?.not_reached },
                                    { status: 'In Contact', count: (leadData as any).data?.in_contact },
                                    { status: 'Appointment', count: (leadData as any).data?.appointment },
                                    { status: 'Lost', count: (leadData as any).data?.lost },
                                    { status: 'New Customer', count: (leadData as any).data?.new_customer },
                                    { status: 'Total Leads', count: (leadData as any).data?.total },
                                ].map((item: { status: string, count: number }, index: number) => (
                                    <div
                                        onMouseEnter={() => setHoveredSlice(item.status)}
                                        onMouseLeave={() => setHoveredSlice(null)}
                                        key={index}
                                        className={`flex justify-between gap-3 items-center transition-colors rounded-[8px] py-[10px] px-[20px]
                                            ${item.status === 'Total Leads' ? 'col-span-2' : 'col-span-2 sm:col-span-1'} ${item.status === hoveredSlice
                                                ? 'text-white bg-[#EFBE12] border-[#EFBE12]'
                                                : 'text-primaryTextColor border border-[#F2F4F7]'} `
                                        }>
                                        <div className="font-inter-medium">
                                            {t(item.status)}
                                        </div>
                                        <div className="font-inter-bold">
                                            {item.count}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </ConditionalRender>

                )}
            </div>
        </DashboardBoxLayout>
    );
};

export default LeadStatistics;
